import axios, { AxiosInstance } from "axios";
import { Constants } from "../../utils/constants";
import logger from "../../utils/logger";
import { ApiResponse } from "../../types";
import { UserLocation } from "../../utils/locationUtils";
import { BROWSE_COUNTRY_KEY, getDynamicSupportedCountries } from "../../components/CountryPickerBanner";
import { getCurrencyForCountry } from "../../utils/countryCurrencyMap";

// Types for Team Management API
export interface TeamMember {
  id: number;
  joinedAt: string;
  role: "ADMIN" | "MEMBER" | "OWNER";
  user: {
    id: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    imageUrl?: string;
  };
}

export interface Team {
  id: number;
  name: string;
  description?: string;
  organizationId: number;
  members: TeamMember[];
  createdAt: string;
  updatedAt: string;
}

export interface TeamCreatePayload {
  name: string;
  description?: string;
  members?: Array<{
    userId: string;
    role: "ADMIN" | "MEMBER" | "OWNER";
  }>;
}

export interface TeamUpdatePayload {
  name?: string;
  description?: string;
  members?: Array<{
    userId: string;
    role: "ADMIN" | "MEMBER" | "OWNER";
    remove?: boolean;
  }>;
}

export interface TeamSearchParams {
  page?: number;
  size?: number;
  pageNumber?: number; // Legacy support
  pageSize?: number; // Legacy support
  offset?: number;
  paged?: boolean;
  unpaged?: boolean;
  "sort.sorted"?: boolean;
  "sort.unsorted"?: boolean;
  name?: string;
  description?: string;
  userId?: string;
  memberRole?: string;
  createdAfter?: string;
  createdBefore?: string;
  ownership?: "CREATED" | "JOINED" | "ALL";
  status?: "ACTIVE" | "DISSOLVED" | string;
}

export interface PaginatedTeamResponse {
  content: Team[];
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  pageable: {
    offset: number;
    pageNumber: number;
    pageSize: number;
    paged: boolean;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    unpaged: boolean;
  };
  size: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  totalElements: number;
  totalPages: number;
}

class TeamsService {
  private api: AxiosInstance;

  /**
   * Get user profile from localStorage
   */
  private getUserProfile(): any {
    try {
      const userStr = localStorage.getItem("user");
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      logger.error("Error parsing user profile:", error);
      return null;
    }
  }

  /**
   * Add context headers to API requests (same as spaceManagementService)
   */
  private addContextHeaders(config: any): void {
    const cachedLocation = this.getCachedLocation();
    const user = this.getUserProfile();
    const isPlatformAdmin = user?.organization?.type?.code === "PLATFORM_ADMIN";

    // Get Timezone Name first as it's the primary fallback
    let timezoneName = "Africa/Nairobi";
    try {
      timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone || "Africa/Nairobi";
      config.headers["X-Timezone-Name"] = timezoneName;
    } catch (error) {
      config.headers["X-Timezone-Name"] = "Africa/Nairobi";
    }

    // X-Timezone-Offset: Always from Browser
    try {
      const offset = new Date().getTimezoneOffset() * -1;
      config.headers["X-Timezone-Offset"] = String(offset);
    } catch (error) {
      config.headers["X-Timezone-Offset"] = "180";
    }

    // X-Locale: Always from Browser
    config.headers["X-Locale"] = navigator.language || "en-KE";

    if (isPlatformAdmin) {
      return;
    }

    // X-Country-Code Priority:
    //  1. User-selected browse country (userBrowseCountry) — the country picker
    //  2. Cached GPS location, if it is an operating country
    //  3. Nothing — header omitted, and the UI shows the country picker
    //
    // The browser location prompt is the only location signal. There is no
    // timezone inference and no IP lookup, so we never guess a market.
    let browseCountryCode: string | null = null;
    let browseCity: string | null = null;
    try {
      const saved = localStorage.getItem(BROWSE_COUNTRY_KEY);
      if (saved) {
        const match = getDynamicSupportedCountries().find(c => c.code === saved);
        if (match) {
          browseCountryCode = match.code;
          browseCity        = match.defaultCity || null;
        }
      }
    } catch {
      // ignore
    }

    const supportedCodes = new Set(getDynamicSupportedCountries().map(c => c.code));
    const isSupported = (code?: string | null) => !!code && supportedCodes.has(code.toUpperCase() as any);

    const countryCode =
      browseCountryCode ||
      (cachedLocation?.countryCode && isSupported(cachedLocation.countryCode) ? cachedLocation.countryCode : null) ||
      null;

    if (countryCode) {
      config.headers["X-Country-Code"] = countryCode;

      // X-City Priority: BrowseCountry → Cached GPS → Country default
      const matchCountry = getDynamicSupportedCountries().find(c => c.code?.toUpperCase() === countryCode?.toUpperCase());
      config.headers["X-City"] =
        browseCity ||
        cachedLocation?.city ||
        matchCountry?.defaultCity ||
        (countryCode === "GH" ? "Accra" : "Nairobi");

      // X-Currency-Code Priority: localStorage → resolved country's currency
      const userCurrencyAttr = localStorage.getItem("userCurrency");
      const explicitCurrency =
        userCurrencyAttr && userCurrencyAttr !== "null" && userCurrencyAttr !== "undefined" ? userCurrencyAttr : null;

      const defaultCurrency = matchCountry?.currency || getCurrencyForCountry(countryCode);
      config.headers["X-Currency-Code"] = explicitCurrency || defaultCurrency;
    } else {
      // Unknown market — send no country context rather than defaulting to one.
      // These are shared axios instances, so clear any earlier value too.
      delete config.headers["X-Country-Code"];
      delete config.headers["X-City"];
      delete config.headers["X-Currency-Code"];
      logger.debug(
        "No operating country resolved (no browse selection or GPS) — omitting country headers; picker expected"
      );
    }

    // X-Latitude & X-Longitude: Optional
    if (cachedLocation?.latitude && cachedLocation?.longitude) {
      config.headers["X-Latitude"] = String(cachedLocation.latitude);
      config.headers["X-Longitude"] = String(cachedLocation.longitude);
    }
  }

  /**
   * Get cached location from localStorage
   */
  private getCachedLocation(): UserLocation | null {
    try {
      const locationStr = localStorage.getItem("userLocation");
      if (locationStr) {
        const location = JSON.parse(locationStr);
        if (location.timestamp && Date.now() - location.timestamp < 6 * 60 * 60 * 1000) {
          return location;
        }
      }
    } catch (error) {
      logger.error("Error parsing cached location:", error);
    }
    return null;
  }

  constructor() {
    // Create authenticated API instance with same base URL as spaceManagementService
    this.api = axios.create({
      baseURL: Constants.SPACE_MANAGEMENT_API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add auth token interceptor
    this.api.interceptors.request.use(config => {
      let token = localStorage.getItem("authToken");
      if (!token) {
        token = localStorage.getItem("access_token");
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add context headers for backend
      this.addContextHeaders(config);

      return config;
    });

    // Add response interceptor
    this.api.interceptors.response.use(
      response => ({
        data: response.data,
        status: response.status,
        message: response.data?.message,
      }),
      error => {
        logger.error("TeamsService API Error:", error);
        throw error;
      }
    );
  }

  /**
   * Get paginated list of teams
   * GET /api/v2/teams
   */
  public async getTeams(params?: TeamSearchParams): Promise<ApiResponse<PaginatedTeamResponse>> {
    try {
      logger.log("Getting teams with params:", params);

      const queryParams = new URLSearchParams();
      if (params) {
        // Convert pageNumber/pageSize to page/size if provided
        const page = params.page !== undefined ? params.page : params.pageNumber;
        const size = params.size !== undefined ? params.size : params.pageSize;

        if (page !== undefined && page !== null) {
          queryParams.append("page", String(page));
        }
        if (size !== undefined && size !== null) {
          queryParams.append("size", String(size));
        }

        // Add other params (excluding pageNumber/pageSize to avoid duplicates)
        Object.entries(params).forEach(([key, value]) => {
          if (
            key !== "page" &&
            key !== "size" &&
            key !== "pageNumber" &&
            key !== "pageSize" &&
            value !== undefined &&
            value !== null &&
            value !== ""
          ) {
            queryParams.append(key, String(value));
          }
        });

        // Ensure status=ACTIVE is included by default if not provided
        if (!queryParams.has("status")) {
          queryParams.append("status", "ACTIVE");
        }
      } else {
        // Default to ACTIVE if no params provided
        queryParams.append("status", "ACTIVE");
      }

      const url = queryParams.toString() ? `/api/v2/teams?${queryParams.toString()}` : `/api/v2/teams`;
      const response = await this.api.get(url);

      logger.log("Get teams response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting teams:", error);
      throw error;
    }
  }

  /**
   * Create a new team with optional members and roles
   * POST /api/v2/teams
   */
  public async createTeam(payload: TeamCreatePayload): Promise<ApiResponse<Team>> {
    try {
      logger.log("Creating team with payload:", payload);

      const response = await this.api.post("/api/v2/teams", payload);

      logger.log("Create team response:", response);
      return response;
    } catch (error) {
      logger.error("Error creating team:", error);
      throw error;
    }
  }

  /**
   * Get team by id
   * GET /api/v2/teams/{id}
   */
  public async getTeamById(id: number): Promise<ApiResponse<Team>> {
    try {
      logger.log("Getting team by id:", id);

      const response = await this.api.get(`/api/v2/teams/${id}`);

      logger.log("Get team by id response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting team by id:", error);
      throw error;
    }
  }

  /**
   * Delete a team and its members
   * DELETE /api/v2/teams/{id}
   */
  public async deleteTeam(id: number): Promise<ApiResponse<void>> {
    try {
      logger.log("Deleting team with id:", id);

      const response = await this.api.delete(`/api/v2/teams/${id}`);

      logger.log("Delete team response:", response);
      return response;
    } catch (error) {
      logger.error("Error deleting team:", error);
      throw error;
    }
  }

  /**
   * Partially update a team (PATCH)
   * PATCH /api/v2/teams/{id}
   */
  public async updateTeam(id: number, payload: TeamUpdatePayload): Promise<ApiResponse<Team>> {
    try {
      logger.log("Updating team with id:", id, "payload:", payload);

      const response = await this.api.patch(`/api/v2/teams/${id}`, payload);

      logger.log("Update team response:", response);
      return response;
    } catch (error) {
      logger.error("Error updating team:", error);
      throw error;
    }
  }

  /**
   * Search teams using query parameters
   * GET /api/v2/teams/search
   */
  public async searchTeams(params?: TeamSearchParams): Promise<ApiResponse<PaginatedTeamResponse>> {
    try {
      logger.log("Searching teams with params:", params);

      const queryParams = new URLSearchParams();
      if (params) {
        // Convert pageNumber/pageSize to page/size if provided
        const page = params.page !== undefined ? params.page : params.pageNumber;
        const size = params.size !== undefined ? params.size : params.pageSize;

        if (page !== undefined && page !== null) {
          queryParams.append("page", String(page));
        }
        if (size !== undefined && size !== null) {
          queryParams.append("size", String(size));
        }

        // Add other params (excluding pageNumber/pageSize to avoid duplicates)
        Object.entries(params).forEach(([key, value]) => {
          if (
            key !== "page" &&
            key !== "size" &&
            key !== "pageNumber" &&
            key !== "pageSize" &&
            value !== undefined &&
            value !== null &&
            value !== ""
          ) {
            queryParams.append(key, String(value));
          }
        });

        // Ensure status=ACTIVE is included by default if not provided
        if (!queryParams.has("status")) {
          queryParams.append("status", "ACTIVE");
        }
      } else {
        // Default to ACTIVE if no params provided
        queryParams.append("status", "ACTIVE");
      }

      const url = queryParams.toString() ? `/api/v2/teams/search?${queryParams.toString()}` : `/api/v2/teams/search`;
      const response = await this.api.get(url);

      logger.log("Search teams response:", response);
      return response;
    } catch (error) {
      logger.error("Error searching teams:", error);
      throw error;
    }
  }
}

export const teamsService = new TeamsService();
export default teamsService;
